import React, { useState, useEffect } from "react";

import { CircleX, MailX, Plus } from "lucide-react";

import firestoreService from "../../services/firestoreService";
import { where } from "firebase/firestore";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";

import { useToast } from "../../components/hooks/use-toast";

import CreateInviteModal from "../../components/CreateInviteModal";
import { CommonDialog } from "../../components/CommonDialog";

const ApplicationInvites = () => {
  const [invites, setInvites] = useState([]);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const [cancelInviteDialogOpen, setCancelInviteDialogOpen] = useState(false);
  const [cancelingInvite, setCancelingInvite] = useState(null);

  const { toast } = useToast();

  useEffect(() => {
    const unsubscribe = firestoreService.subscribeToCollection(
      "invites",
      [where("type", "==", "application"), where("status", "==", "pending")],
      setInvites
    );

    // Cleanup the listener on component unmount
    return () => unsubscribe();
  }, []);

  const handleInviteUser = () => {
    setTimeout(() => {
      setInviteModalOpen(true);
    }, 200);
  };

  const handleCancelInvite = async (invite) => {
    try {
      if (invite.status === "pending") {
        await firestoreService.update("invites", invite.id, {
          status: "canceled",
        });

        toast({
          title: "Invite canceled",
          description: `The invite to "${invite.email}" was successfully canceled.`,
        });
      }
    } catch (error) {
      console.error("Error canceling the invite:", error);

      toast({
        variant: "destructive",
        title: "Error canceling the invite",
        description: error.message,
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invites</CardTitle>
        <CardDescription>Invite users to join the application.</CardDescription>
      </CardHeader>

      <CreateInviteModal
        open={inviteModalOpen}
        setOpen={setInviteModalOpen}
        inviteType={"application"}
      />

      <CommonDialog
        open={cancelInviteDialogOpen}
        setOpen={setCancelInviteDialogOpen}
        title={`Cancel invite for "${cancelingInvite?.email}"?`}
        description={
          "This invite to join the application will be canceled. This cannot be undone. Proceed?"
        }
        onConfirm={() => {
          handleCancelInvite(cancelingInvite);
        }}
      />

      <CardContent>
        <Button className="min-w-[135px]" onClick={handleInviteUser}>
          <Plus size={16} className="mr-2" />
          Invite User
        </Button>

        <div className="flex flex-col gap-6 overflow-y-auto p-4">
          {invites.length > 0 ? (
            invites.map((invite, index) => (
              <div
                key={index}
                className="flex items-center justify-between space-x-4"
              >
                <div className="flex items-center space-x-4">
                  <div>
                    <p className="text-md font-medium leading-none capitalize">
                      {invite.applicationRole}
                    </p>

                    <p className="text-sm text-muted-foreground mt-1">
                      {invite.email}
                    </p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="max-w-[150px]"
                  onClick={() => {
                    setCancelingInvite(invite);
                    setCancelInviteDialogOpen(true);
                  }}
                >
                  <CircleX size={16} className="mr-2" />
                  Cancel
                </Button>
              </div>
            ))
          ) : (
            <div className="flex items-center justify-center text-sm font-semibold space-x-2 mt-6">
              <MailX className="w-5 h-5" />
              <span>No pending invites</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ApplicationInvites;
